// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { getAuthToken } from '../../utils/auth';
import './KnowMe.css';

const COLORS = ['#d2e823', '#f87171', '#60a5fa', '#a78bfa', '#fb923c', '#34d399', '#f472b6', '#fbbf24', '#1a3a1a', '#334155'];

const getSocialIcon = (title, url) => {
    const t = (title || '').toLowerCase();
    const u = (url || '').toLowerCase();
    if (t.includes('instagram') || u.includes('instagram.com')) return <i className="fi fi-brands-instagram"></i>;
    if (t.includes('threads') || u.includes('threads.net')) return <i className="fi fi-brands-threads"></i>;
    if (t.includes('twitter') || t.includes('x.com') || u.includes('twitter.com') || u.includes('x.com')) return <i className="fi fi-brands-twitter-alt"></i>;
    if (t.includes('facebook') || u.includes('facebook.com')) return <i className="fi fi-brands-facebook"></i>;
    if (t.includes('youtube') || u.includes('youtube.com') || u.includes('youtu.be')) return <i className="fi fi-brands-youtube"></i>;
    return null;
};

function KnowMe() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('links');
    const [designSubTab, setDesignSubTab] = useState('Header');
    const [isLinktreeExpanded, setIsLinktreeExpanded] = useState(true);
    const [showHome, setShowHome] = useState(window.innerWidth < 1024); // Show home view by default on mobile
    const [newTitle, setNewTitle] = useState('');
    const [newUrl, setNewUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const [username, setUsername] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [bio, setBio] = useState('');
    const [bgColor, setBgColor] = useState('#d2e823');
    const [btnColor, setBtnColor] = useState('#1a3a1a');
    const [btnFontColor, setBtnFontColor] = useState('#ffffff');
    const [btnStyle, setBtnStyle] = useState('rounded');
    const [selectedTheme, setSelectedTheme] = useState('Air White');
    const [bgStyle, setBgStyle] = useState('Flat Color');
    const [fontFamily, setFontFamily] = useState('Inter (Default)');
    const [fontSize, setFontSize] = useState('Medium');
    const [btnShadow, setBtnShadow] = useState('Soft');
    const [showProfilePopup, setShowProfilePopup] = useState(false);
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const getToken = () => getAuthToken();

    const headers = useCallback(() => ({
        'Authorization': 'Bearer ' + getToken(),
        'Content-Type': 'application/json',
    }), []);

    const fetchData = useCallback(async () => {
        try {
            const [profileRes, linksRes] = await Promise.all([
                fetch(API_ENDPOINTS.KNOWME.PROFILE, { headers: headers() }),
                fetch(API_ENDPOINTS.KNOWME.LINKS, { headers: headers() }),
            ]);
            const profileData = await profileRes.json();
            const linksData = await linksRes.json();

            if (profileData) {
                setProfile(profileData);
                setUsername(profileData.username || '');
                setDisplayName(profileData.displayName || '');
                setBio(profileData.bio || '');
                setBgColor(profileData.backgroundColor || '#d2e823');
                setBtnColor(profileData.buttonColor || '#1a3a1a');
                setBtnFontColor(profileData.buttonFontColor || '#ffffff');
                setBtnStyle(profileData.buttonStyle || 'rounded');
            }
            if (Array.isArray(linksData)) setLinks(linksData);
        } catch (err) {
            console.error('Failed to load KnowMe data:', err);
        }
        setLoading(false);
    }, [headers]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const saveProfile = async () => {
        setSaving(true);
        try {
            const res = await fetch(API_ENDPOINTS.KNOWME.PROFILE, {
                method: 'PUT', headers: headers(),
                body: JSON.stringify({ username, displayName, bio, backgroundColor: bgColor, buttonColor: btnColor, buttonFontColor: btnFontColor, buttonStyle: btnStyle }),
            });
            const data = await res.json();
            if (!res.ok) { alert(data.message || 'Error'); setSaving(false); return; }
            setProfile(data);
        } catch (err) { console.error(err); }
        setSaving(false);
    };

    const addLink = async () => {
        if (!newTitle.trim() || !newUrl.trim()) return;
        try {
            const res = await fetch(API_ENDPOINTS.KNOWME.LINKS, {
                method: 'POST', headers: headers(),
                body: JSON.stringify({ title: newTitle, url: newUrl }),
            });
            const link = await res.json();
            setLinks(prev => [...prev, link]);
            setNewTitle('');
            setNewUrl('');
            setActiveTab('links');
            setShowHome(false);
        } catch (err) { console.error(err); }
    };

    const deleteLink = async (id) => {
        try {
            await fetch(API_ENDPOINTS.KNOWME.LINK(id), { method: 'DELETE', headers: headers() });
            setLinks(prev => prev.filter(l => l._id !== id));
        } catch (err) { console.error(err); }
    };

    const toggleLink = async (id, active) => {
        try {
            await fetch(API_ENDPOINTS.KNOWME.LINK(id), {
                method: 'PUT', headers: headers(),
                body: JSON.stringify({ active: !active }),
            });
            setLinks(prev => prev.map(l => l._id === id ? { ...l, active: !active } : l));
        } catch (err) { console.error(err); }
    };

    const handleDragStart = (index) => { dragItem.current = index; };
    const handleDragEnter = (index) => { dragOverItem.current = index; };
    const handleDragEnd = async () => {
        const items = [...links];
        const [dragged] = items.splice(dragItem.current, 1);
        items.splice(dragOverItem.current, 0, dragged);
        setLinks(items);
        dragItem.current = null;
        dragOverItem.current = null;
        try {
            await fetch(API_ENDPOINTS.KNOWME.LINKS_REORDER, {
                method: 'PUT', headers: headers(),
                body: JSON.stringify({ orderedIds: items.map(l => l._id) }),
            });
        } catch (err) { console.error(err); }
    };

    const btnRadius = btnStyle === 'pill' ? '9999px' : btnStyle === 'square' ? '0' : '12px';

    if (loading) return <div className="km-loading">Loading...</div>;

    return (
        <div className="km-dashboard">
            <Helmet>
                <title>Know Me | Hari Vishnu</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>

            {/* ── Sidebar (Desktop) ── */}
            <aside className="km-sidebar">
                <div className="km-sidebar-header relative" onClick={() => setShowProfilePopup(!showProfilePopup)}>
                    <div className="km-avatar-large w-[40px] h-[40px] text-[1.2rem] mb-0">{(displayName || username || '?')[0].toUpperCase()}</div>
                    <span className="font-[800] text-[0.9rem] truncate">{username}</span>
                    <i className="fi fi-rr-angle-small-down ml-auto"></i>

                    {showProfilePopup && (
                        <div className="km-profile-popup" onClick={(e) => e.stopPropagation()}>
                            <div className="km-popup-header">
                                <div className="km-avatar-large w-[48px] h-[48px] text-[1.4rem] mb-0">{(displayName || username || '?')[0].toUpperCase()}</div>
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-2">
                                        <span className="font-[800] text-[0.95rem]">{username}</span>
                                        <span className="km-pro-badge">Pro</span>
                                    </div>
                                    <span className="text-[0.75rem] text-[#666]">linktr.ee/{username}</span>
                                </div>
                            </div>
                            
                            <div className="km-popup-divider"></div>
                            
                            <div className="km-popup-item" onClick={() => setActiveTab('design')}>
                                <i className="fi fi-rr-plus"></i> Create new Linktree
                            </div>
                            
                            <div className="km-popup-divider"></div>
                            
                            <div className="km-popup-item" onClick={() => setActiveTab('design')}>
                                <i className="fi fi-rr-user"></i> Account
                            </div>
                            <div className="km-popup-item">
                                <i className="fi fi-rr-bolt"></i> Keep Pro access
                            </div>
                            
                            <div className="km-popup-divider"></div>
                            
                            <div className="km-popup-item"><i className="fi fi-rr-interrogation"></i> Ask a question</div>
                            <div className="km-popup-item"><i className="fi fi-rr-document"></i> Help topics</div>
                            <div className="km-popup-item"><i className="fi fi-rr-bulb"></i> Share feedback</div>
                            
                            <div className="km-popup-divider"></div>
                            
                            <div className="km-popup-item text-red-500" onClick={() => window.location.href = '/login'}>
                                <i className="fi fi-rr-exit"></i> Log out
                            </div>
                        </div>
                    )}
                </div>
                <div className="km-sidebar-nav">
                    <div className="km-nav-section">
                        <div className={`km-nav-item ${isLinktreeExpanded ? 'active' : ''}`} onClick={() => setIsLinktreeExpanded(!isLinktreeExpanded)}>
                            <i className="fi fi-rr-apps"></i> My Linktree
                            <i className={`fi fi-rr-angle-small-${isLinktreeExpanded ? 'up' : 'down'} ml-auto`}></i>
                        </div>
                        {isLinktreeExpanded && (
                            <div className="km-sub-nav">
                                <div className={`km-sub-nav-item ${activeTab === 'links' && !showHome ? 'active' : ''}`} onClick={() => { setActiveTab('links'); setShowHome(false); }}>Links</div>
                                <div className={`km-sub-nav-item ${activeTab === 'design' ? 'active' : ''}`} onClick={() => { setActiveTab('design'); setShowHome(false); }}>Design</div>
                            </div>
                        )}
                    </div>

                    <div className={`km-nav-item ${activeTab === 'earn' ? 'active' : ''}`} onClick={() => setActiveTab('earn')}>
                        <i className="fi fi-rr-coins"></i> Earn
                        <i className="fi fi-rr-angle-small-down ml-auto"></i>
                    </div>
                    <div className={`km-nav-item ${activeTab === 'audience' ? 'active' : ''}`} onClick={() => setActiveTab('audience')}>
                        <i className="fi fi-rr-users"></i> Audience
                    </div>
                    <div className={`km-nav-item ${activeTab === 'insights' ? 'active' : ''}`} onClick={() => setActiveTab('insights')}>
                        <i className="fi fi-rr-stats"></i> Insights
                    </div>

                    <div className="km-nav-section-title">Tools</div>
                    <div className="km-nav-item"><i className="fi fi-rr-calendar"></i> Social planner</div>
                    <div className="km-nav-item"><i className="fi fi-rr-comment"></i> Instagram auto-reply</div>
                    <div className="km-nav-item"><i className="fi fi-rr-link-alt"></i> Link shortener</div>
                    <div className="km-nav-item"><i className="fi fi-rr-bulb"></i> Post ideas</div>
                </div>

                <div className="km-setup-card">
                    <div className="km-setup-header">
                        <div className="km-progress-circle">33%</div>
                        <div className="km-setup-info">
                            <div className="km-setup-title">Your setup checklist</div>
                            <div className="text-[0.7rem] text-[#666]">2 of 6 complete</div>
                        </div>
                    </div>
                    <button className="km-btn-setup">Finish setup</button>
                </div>
            </aside>

            {/* ── Main Content ── */}
            <main className="km-main">
                <div className="km-topbar">
                    <div className="flex items-center gap-[12px]">
                        {showHome && <div className="km-avatar-large w-[32px] h-[32px] mb-0 text-[1rem]">{(displayName || username || '?')[0].toUpperCase()}</div>}
                        <span className="km-view-title">{showHome ? username : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</span>
                    </div>
                    <div className="flex items-center gap-[16px]">
                        {showHome ? (
                            <>
                                <button className="rounded-full border border-purple-200 px-[12px] py-[6px] text-[0.85rem] font-[700] text-purple-600">Keep Pro access</button>
                                <i className="fi fi-rr-share text-[1.2rem]"></i>
                                <i className="fi fi-rr-bell text-[1.2rem]"></i>
                            </>
                        ) : (
                            <>
                                <button className="km-manage-btn !rounded-full !py-[6px] !px-[12px]"><i className="fi fi-rr-magic-wand"></i> Enhance</button>
                                <i className="fi fi-rr-settings text-[1.2rem]"></i>
                            </>
                        )}
                    </div>
                </div>

                <div className="km-container">
                        {showHome ? (
                            /* ── Home Page (Mobile view design) ── */
                            <div className="km-overview">
                                <div className="flex flex-col items-center mb-[24px]">
                                    <span className="text-[1.2rem] font-[800] mb-[4px]">{username}</span>
                                    <div className="flex items-center gap-[6px] text-[#666] text-[0.9rem]">
                                        linktr.ee/{username} <i className="fi fi-rr-copy"></i>
                                    </div>
                                </div>

                                <div className="flex gap-[12px] mb-[24px]">
                                    <button className="km-manage-btn flex-1 !rounded-full" onClick={() => { setActiveTab('links'); setShowHome(false); }}><i className="fi fi-rr-apps-add"></i> Links</button>
                                    <button className="km-manage-btn flex-1 !rounded-full" onClick={() => { setActiveTab('design'); setShowHome(false); }}><i className="fi fi-rr-palette"></i> Design</button>
                                </div>

                                <div className="km-overview-grid" style={{ gridTemplateColumns: '1fr' }}>
                                    <div className="km-block-card" onClick={() => { setActiveTab('links'); setShowHome(false); }}>
                                        <div className="km-block-preview">
                                            <div className="km-public-avatar-lite" style={{ borderColor: btnColor }}>
                                                <span style={{ color: btnColor }}>{(displayName || username || '?')[0].toUpperCase()}</span>
                                            </div>
                                            <span className="mt-[8px] text-[0.8rem] font-[800]" style={{ color: btnColor }}>{displayName || username}</span>
                                            <div className="mt-[12px] w-[120px] rounded-full h-[24px] bg-white text-[0.6rem] flex items-center justify-center font-[700] shadow-sm border border-black/5">Join {username} on Linktree</div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="km-block-title">Edit Links</span>
                                            <i className="fi fi-rr-arrow-right"></i>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-[40px] flex justify-center">
                                    <div className="km-progress-circle w-[80px] h-[80px] text-[1.1rem]">33% <br/><span className="text-[0.5rem]">set up</span></div>
                                </div>
                            </div>
                        ) : activeTab === 'links' ? (
                            /* ── Links Management View ── */
                            <div className="km-links-view">
                                <div className="km-profile-header">
                                    <div className="km-avatar-large">{(displayName || username || '?')[0].toUpperCase()}</div>
                                    <span className="km-profile-name">{displayName || username}</span>
                                    <span className="km-profile-bio">{bio || 'd'}</span>
                                    <div className="flex items-center justify-center gap-[12px] mt-[10px] text-[1.4rem] text-[#888]">
                                        <i className="fi fi-brands-instagram cursor-pointer hover:text-km-purple"></i>
                                        <i className="fi fi-brands-youtube cursor-pointer hover:text-km-purple"></i>
                                        <i className="fi fi-brands-facebook cursor-pointer hover:text-km-purple"></i>
                                        <i className="fi fi-rr-plus-circle cursor-pointer hover:text-km-purple"></i>
                                    </div>
                                </div>

                                <button className="km-btn-add" onClick={() => { setNewTitle(''); setNewUrl(''); /* toggle modal or inline form */ }}>
                                    <i className="fi fi-rr-plus"></i> Add
                                </button>

                                <div className="km-manage-links-row">
                                    <button className="km-manage-btn"><i className="fi fi-rr-folder-add"></i> Add collection</button>
                                    <button className="km-manage-btn">View archive <i className="fi fi-rr-angle-small-right"></i></button>
                                </div>

                                <div className="space-y-[12px]">
                                    {links.map((link, i) => (
                                        <div key={link._id} className="km-card"
                                            draggable onDragStart={() => handleDragStart(i)} onDragEnter={() => handleDragEnter(i)} onDragEnd={handleDragEnd} onDragOver={e => e.preventDefault()}>
                                            <div className="km-card-header">
                                                <div className="km-card-grip"><i className="fi fi-rr-menu-dots-vertical"></i></div>
                                                <div className="km-card-info">
                                                    <span className="km-card-title">{link.title} <i className="fi fi-rr-edit text-[0.75rem] text-km-purple/40"></i></span>
                                                    <span className="km-card-url">{link.url} <i className="fi fi-rr-edit text-[0.75rem] text-km-purple/40"></i></span>
                                                </div>
                                                <div className="flex items-center gap-[12px]">
                                                    <i className="fi fi-rr-share-alt text-[#888]"></i>
                                                    <div className={`km-toggle ${link.active ? 'active' : ''}`} onClick={() => toggleLink(link._id, link.active)}>
                                                        <div className="km-toggle-dot"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="km-card-actions">
                                                <div className="km-card-tool">{getSocialIcon(link.title, link.url) || <i className="fi fi-rr-picture"></i>}</div>
                                                <div className="km-card-tool"><i className="fi fi-rr-star"></i></div>
                                                <div className="km-card-tool"><i className="fi fi-rr-calendar"></i></div>
                                                <div className="km-card-tool"><i className="fi fi-rr-stats"></i> {link.clicks || 0} clicks</div>
                                                <div className="km-card-tool ml-auto text-[#f87171]" onClick={() => deleteLink(link._id)}><i className="fi fi-rr-trash"></i></div>
                                            </div>
                                        </div>
                                    ))}
                                    {links.length === 0 && <p className="text-center py-[20px] text-[#888]">No links yet. Click "Add" to start.</p>}
                                </div>
                            </div>
                        ) : activeTab === 'design' ? (
                            /* ── Design View ── */
                            <div className="km-design-view">
                                <aside className="km-design-side-nav">
                                    {[
                                        { id: 'Header', icon: 'fi-rr-user' },
                                        { id: 'Theme', icon: 'fi-rr-palette' },
                                        { id: 'Wallpaper', icon: 'fi-rr-layers' },
                                        { id: 'Text', icon: 'fi-rr-t' },
                                        { id: 'Buttons', icon: 'fi-rr-rectangle-horizontal' },
                                        { id: 'Colors', icon: 'fi-rr-tint' },
                                        { id: 'Footer', icon: 'fi-rr-settings-sliders' }
                                    ].map(sub => (
                                        <div key={sub.id} className={`km-design-nav-item ${designSubTab === sub.id ? 'active' : ''}`} onClick={() => setDesignSubTab(sub.id)}>
                                            <i className={`fi ${sub.icon}`}></i> {sub.id}
                                        </div>
                                    ))}
                                </aside>
                                <div className="km-design-content">
                                    {designSubTab === 'Header' && (
                                        <div className="km-header-settings">
                                            <div className="km-design-section">
                                                <label className="km-design-label">Profile image</label>
                                                <div className="km-image-upload-row">
                                                    <div className="km-avatar-large w-[80px] h-[80px] mb-0">{(displayName || username || '?')[0].toUpperCase()}</div>
                                                    <button className="km-btn-add-img"><i className="fi fi-rr-plus"></i> Add</button>
                                                </div>
                                            </div>

                                            <div className="km-design-section">
                                                <label className="km-design-label">Profile image layout</label>
                                                <div className="km-design-toggle-row">
                                                    <button className="km-design-toggle-btn active">
                                                        <i className="fi fi-rr-circle-user"></i>
                                                        <span>Classic</span>
                                                    </button>
                                                    <button className="km-design-toggle-btn">
                                                        <i className="fi fi-rr-expand"></i>
                                                        <span className="flex items-center gap-2">Hero <i className="fi fi-rr-bolt text-yellow-500 text-[0.7rem]"></i></span>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="km-design-section">
                                                <label className="km-design-label">Title</label>
                                                <input className="km-design-input" placeholder="Your Title" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                                            </div>

                                            <div className="km-design-section">
                                                <label className="km-design-label">Title style</label>
                                                <div className="km-design-toggle-row">
                                                    <button className="km-design-toggle-btn active">
                                                        <span className="text-[1.2rem] font-bold">Aa</span>
                                                        <span>Text</span>
                                                    </button>
                                                    <button className="km-design-toggle-btn">
                                                        <i className="fi fi-rr-picture"></i>
                                                        <span className="flex items-center gap-2">Logo <i className="fi fi-rr-bolt text-yellow-500 text-[0.7rem]"></i></span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {designSubTab === 'Theme' && (
                                        <div className="km-theme-settings">
                                            <label className="km-design-label">Select a Theme</label>
                                            <div className="grid grid-cols-2 gap-4">
                                                {['Air White', 'Air Black', 'Mineral Blue', 'Mineral Green'].map(t => (
                                                    <div key={t} className={`km-theme-preset p-4 rounded-xl border cursor-pointer transition-all ${selectedTheme === t ? 'border-km-purple ring-2 ring-km-purple/10' : 'border-km-border'}`} onClick={() => setSelectedTheme(t)}>
                                                        <div className={`h-[100px] rounded-lg mb-2 ${t.includes('Black') ? 'bg-black' : t.includes('Blue') ? 'bg-blue-50' : 'bg-gray-50'}`}></div>
                                                        <span className={`text-[0.8rem] font-bold ${selectedTheme === t ? 'text-km-purple' : ''}`}>{t}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {designSubTab === 'Wallpaper' && (
                                        <div className="km-wallpaper-settings">
                                            <label className="km-design-label">Background Style</label>
                                            <div className="km-design-toggle-row mb-6">
                                                {['Flat Color', 'Gradient'].map(s => (
                                                    <button key={s} className={`km-design-toggle-btn ${bgStyle === s ? 'active' : ''}`} onClick={() => setBgStyle(s)}>{s}</button>
                                                ))}
                                            </div>
                                            <label className="km-design-label">Color</label>
                                            <div className="flex gap-2 flex-wrap">
                                                {COLORS.map(c => (
                                                    <div key={c} className={`w-[36px] h-[36px] rounded-full cursor-pointer ring-2 ${bgColor === c ? 'ring-km-purple' : 'ring-transparent'}`} style={{ backgroundColor: c }} onClick={() => setBgColor(c)}></div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {designSubTab === 'Text' && (
                                        <div className="km-text-settings">
                                            <label className="km-design-label">Font Family</label>
                                            <select className="km-design-input mb-4" value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
                                                <option>Inter (Default)</option>
                                                <option>Roboto</option>
                                                <option>Poppins</option>
                                                <option>Lora (Serif)</option>
                                            </select>
                                            <label className="km-design-label">Font Size</label>
                                            <div className="km-design-toggle-row">
                                                {['Small', 'Medium', 'Large'].map(s => (
                                                    <button key={s} className={`km-design-toggle-btn ${fontSize === s ? 'active' : ''}`} onClick={() => setFontSize(s)}>{s}</button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {designSubTab === 'Buttons' && (
                                        <div className="km-buttons-settings">
                                            <label className="km-design-label">Button Shape</label>
                                            <div className="km-design-toggle-row mb-4">
                                                <button className={`km-design-toggle-btn ${btnStyle === 'rounded' ? 'active' : ''}`} onClick={() => setBtnStyle('rounded')}>
                                                    <i className="fi fi-rr-square-rounded"></i>
                                                    <span>Rounded</span>
                                                </button>
                                                <button className={`km-design-toggle-btn ${btnStyle === 'pill' ? 'active' : ''}`} onClick={() => setBtnStyle('pill')}>
                                                    <i className="fi fi-rr-capsule"></i>
                                                    <span>Pill</span>
                                                </button>
                                                <button className={`km-design-toggle-btn ${btnStyle === 'square' ? 'active' : ''}`} onClick={() => setBtnStyle('square')}>
                                                    <i className="fi fi-rr-square"></i>
                                                    <span>Square</span>
                                                </button>
                                            </div>
                                            <label className="km-design-label">Shadow</label>
                                            <div className="km-design-toggle-row">
                                                {['None', 'Soft', 'Hard'].map(s => (
                                                    <button key={s} className={`km-design-toggle-btn ${btnShadow === s ? 'active' : ''}`} onClick={() => setBtnShadow(s)}>{s}</button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {designSubTab === 'Colors' && (
                                        <div className="km-colors-settings">
                                            <div className="flex flex-col gap-6">
                                                <div>
                                                    <label className="km-design-label">Button Color</label>
                                                    <div className="flex gap-2 flex-wrap">
                                                        {COLORS.map(c => (
                                                            <div key={c} className={`w-[36px] h-[36px] rounded-full cursor-pointer ring-2 ${btnColor === c ? 'ring-km-purple' : 'ring-transparent'}`} style={{ backgroundColor: c }} onClick={() => setBtnColor(c)}></div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="km-design-label">Button Text Color</label>
                                                    <div className="flex gap-2">
                                                        <div className={`w-[36px] h-[36px] rounded-full border border-gray-200 cursor-pointer ${btnFontColor === '#ffffff' ? 'ring-2 ring-km-purple' : ''} bg-white`} onClick={() => setBtnFontColor('#ffffff')}></div>
                                                        <div className={`w-[36px] h-[36px] rounded-full border border-gray-200 cursor-pointer ${btnFontColor === '#000000' ? 'ring-2 ring-km-purple' : ''} bg-black`} onClick={() => setBtnFontColor('#000000')}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {designSubTab === 'Footer' && (
                                        <div className="km-footer-settings">
                                            <label className="km-design-label">Footer Text</label>
                                            <input className="km-design-input mb-4" placeholder="Know Me" />
                                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                                <span className="font-bold text-[0.9rem]">Show branding</span>
                                                <div className="km-toggle active"><div className="km-toggle-dot"></div></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : activeTab === 'insights' ? (
                            <div className="km-insights-view p-6 bg-white rounded-3xl border border-km-border">
                                <h2 className="text-[1.5rem] font-bold mb-6">Overview</h2>
                                <div className="grid grid-cols-3 gap-6 mb-8">
                                    <div className="p-6 bg-km-purple-light rounded-2xl">
                                        <span className="text-[0.8rem] text-km-purple font-bold">Total Views</span>
                                        <div className="text-[2rem] font-bold text-km-purple">{profile?.totalViews || 0}</div>
                                    </div>
                                    <div className="p-6 bg-green-50 rounded-2xl">
                                        <span className="text-[0.8rem] text-green-600 font-bold">Total Clicks</span>
                                        <div className="text-[2rem] font-bold text-green-600 font-bold">{links.reduce((s,l)=>s+(l.clicks||0),0)}</div>
                                    </div>
                                    <div className="p-6 bg-blue-50 rounded-2xl">
                                        <span className="text-[0.8rem] text-blue-600 font-bold">CTR</span>
                                        <div className="text-[2rem] font-bold text-blue-600">
                                            {profile?.totalViews ? ((links.reduce((s,l)=>s+(l.clicks||0),0) / profile.totalViews) * 100).toFixed(1) : 0}%
                                        </div>
                                    </div>
                                </div>
                                <div className="h-[200px] bg-gray-50 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-200">
                                    <p className="text-[#888] font-bold">Traffic Chart Placeholder</p>
                                </div>
                            </div>
                        ) : activeTab === 'audience' ? (
                             <div className="km-audience-view p-6 bg-white rounded-3xl border border-km-border">
                                <h2 className="text-[1.5rem] font-bold mb-6">Subscribers</h2>
                                <div className="flex flex-col items-center justify-center py-[60px] text-[#aaa]">
                                    <i className="fi fi-rr-users text-[3rem] mb-4"></i>
                                    <p className="font-bold">No subscribers yet</p>
                                    <p className="text-[0.8rem]">Share your link to grow your audience.</p>
                                    <button className="mt-6 px-6 py-2 bg-km-purple text-white rounded-full font-bold">Export CSV</button>
                                </div>
                             </div>
                        ) : activeTab === 'earn' ? (
                             <div className="km-earn-view p-6 bg-white rounded-3xl border border-km-border">
                                <h2 className="text-[1.5rem] font-bold mb-6">Monetization</h2>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-6 border border-km-border rounded-2xl flex flex-col items-center text-center">
                                        <i className="fi fi-rr-heart text-[2rem] text-red-500 mb-2"></i>
                                        <span className="font-bold">Tip Jar</span>
                                        <p className="text-[0.8rem] text-[#666]">Accept tips from your followers.</p>
                                        <button className="mt-4 px-4 py-1 border border-km-border rounded-full text-[0.8rem] font-bold">Enable</button>
                                    </div>
                                    <div className="p-6 border border-km-border rounded-2xl flex flex-col items-center text-center">
                                        <i className="fi fi-rr-shopping-bag text-[2rem] text-orange-500 mb-2"></i>
                                        <span className="font-bold">Affiliate Links</span>
                                        <p className="text-[0.8rem] text-[#666]">Earn commission on products.</p>
                                        <button className="mt-4 px-4 py-1 border border-km-border rounded-full text-[0.8rem] font-bold">Add Product</button>
                                    </div>
                                </div>
                             </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-[100px] text-[#aaa]">
                                <i className="fi fi-rr-hourglass-end text-[3rem] mb-4"></i>
                                <h2 className="text-[1.5rem] font-bold">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} View</h2>
                                <p>Detailed options for this segment will be available in your plan.</p>
                            </div>
                        )}
                </div>
            </main>

            {/* ── Preview Column (Desktop) ── */}
            <aside className="km-preview-col">
                <div className="flex items-center gap-[12px] mb-[20px] w-full max-w-[320px]">
                    <div className="flex-1 bg-[#f3f3f1] rounded-full px-[16px] py-[8px] text-[0.8rem] font-[600] truncate">linktr.ee/{username}</div>
                    <i className="fi fi-rr-share"></i>
                    <i className="fi fi-rr-settings-sliders"></i>
                </div>
                <div className="km-phone-frame">
                    <div className="km-phone-inner">
                        <div className="km-public-page-lite" style={{ 
                            backgroundColor: bgColor,
                            fontFamily: fontFamily.includes('Lora') ? 'serif' : fontFamily.includes('Roboto') ? 'Roboto, sans-serif' : fontFamily.includes('Poppins') ? 'Poppins, sans-serif' : 'Inter, sans-serif'
                        }}>
                            <div className="km-public-card-lite">
                                <div className="km-public-avatar-lite" style={{ borderColor: btnColor }}>
                                    <span style={{ color: btnColor }}>{(displayName || username || '?')[0].toUpperCase()}</span>
                                </div>
                                <h1 className="km-public-name-lite" style={{ 
                                    color: btnColor,
                                    fontSize: fontSize === 'Large' ? '1.2rem' : fontSize === 'Small' ? '0.8rem' : '1rem'
                                }}>{displayName || username}</h1>
                                {bio && <p className="km-public-bio-lite" style={{ color: btnColor }}>{bio}</p>}

                                <div className="km-public-links-lite">
                                    {links.filter(l => l.active).map(link => (
                                        <div key={link._id} className="km-public-link-btn-lite flex items-center justify-center gap-[8px]"
                                            style={{ 
                                                backgroundColor: btnColor, 
                                                color: btnFontColor, 
                                                borderRadius: btnStyle === 'pill' ? '9999px' : btnStyle === 'square' ? '0' : '12px',
                                                boxShadow: btnShadow === 'Soft' ? '0 4px 10px rgba(0,0,0,0.1)' : btnShadow === 'Hard' ? '4px 4px 0 rgba(0,0,0,0.2)' : 'none',
                                                fontSize: fontSize === 'Large' ? '0.9rem' : fontSize === 'Small' ? '0.7rem' : '0.8rem'
                                            }}>
                                            {getSocialIcon(link.title, link.url)}
                                            {link.title}
                                        </div>
                                    ))}
                                    {links.length === 0 && (
                                        <div className="km-public-link-btn-lite opacity-50" style={{ backgroundColor: btnColor, color: btnFontColor, borderRadius: btnRadius }}>
                                            Your links will appear here
                                        </div>
                                    )}
                                </div>
                                <div className="mt-auto pt-[20px] opacity-40 text-[0.6rem] font-[800]" style={{ color: btnColor }}>Know Me</div>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* ── Mobile Bottom Nav ── */}
            <nav className="km-bottom-nav">
                <div className={`km-mobile-nav-item ${showHome ? 'active' : ''}`} onClick={() => setShowHome(true)}>
                    <i className="fi fi-rr-apps-add"></i>
                    <span>My Linktree</span>
                </div>
                <div className="km-mobile-nav-item">
                    <i className="fi fi-rr-coins"></i>
                    <span>Earn</span>
                </div>
                <div className="km-mobile-nav-item">
                    <i className="fi fi-rr-stats"></i>
                    <span>Insights</span>
                </div>
                <div className="km-mobile-nav-item">
                    <i className="fi fi-rr-users"></i>
                    <span>Audience</span>
                </div>
                <div className="km-mobile-nav-item">
                    <i className="fi fi-rr-menu-burger"></i>
                    <span>More</span>
                </div>
            </nav>
        </div>
    );
}

export default KnowMe;
